import { activeShopsQuery } from "@/lib/route-queries";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { MapPin, Star, ShieldCheck } from "lucide-react";

import { CITIES, type CitySlug, CITY_SLUGS } from "@/lib/cities";
import { SITE_URL, hreflangLinks, ogImageMeta } from "@/lib/seo";
import ogShopsImg from "@/assets/og/og-shops.jpg";
import { pickLocalized, useT } from "@/i18n/LanguageProvider";
import { shopLogoImgProps } from "@/lib/shop-image-src";
import { shopMatchesCity, type DirectoryShop as Shop } from "@/lib/shops-city";
import { DirectoryRouteError, DirectoryPending } from "@/components/common/DirectoryRouteStates";


export const Route = createFileRoute("/shops/city/$city")({
  parseParams: (params) => {
    const city = params.city as string;
    if (!CITY_SLUGS.includes(city as CitySlug)) throw notFound();
    return { city: city as CitySlug };
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(activeShopsQuery),
  head: ({ params, loaderData }) => {
    const c = CITIES[params.city];
    const url = `${SITE_URL}/shops/city/${params.city}`;
    const all = Array.isArray(loaderData) ? (loaderData as Shop[]) : [];
    const shops = all.filter((s) => shopMatchesCity(s, c));
    const title = `Auto & bike parts shops in ${c.name_en} | Jantro Bazar`;
    const desc = `Verified motorcycle and car parts sellers in ${c.name_en}. Compare ratings, specialties and delivery times — ${c.eta}`;
    const collectionLd = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      url,
      name: title,
      description: desc,
      inLanguage: ["en", "bn"],
      isPartOf: { "@type": "WebSite", name: "Jantro Bazar", url: `${SITE_URL}/` },
      about: {
        "@type": "Place",
        name: c.name_en,
        address: { "@type": "PostalAddress", addressLocality: c.name_en, addressCountry: "BD" },
      },
    };
    const breadcrumbLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Shops", item: `${SITE_URL}/shops` },
        { "@type": "ListItem", position: 3, name: c.name_en, item: url },
      ],
    };
    const itemListLd = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      numberOfItems: shops.length,
      itemListElement: shops.slice(0, 20).map((s, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `${SITE_URL}/shops/${s.slug}`,
        name: s.name_en || s.slug,
      })),
    };
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        ...ogImageMeta(`${SITE_URL}${ogShopsImg}`, `Parts shops in ${c.name_en} — Jantro Bazar`),
        // Thin hubs shouldn't be indexed until at least one seller is listed.
        ...(shops.length === 0 ? [{ name: "robots", content: "noindex, follow" }] : []),
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks(url)],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(breadcrumbLd) },
        { type: "application/ld+json", children: JSON.stringify(collectionLd) },
        { type: "application/ld+json", children: JSON.stringify(itemListLd) },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="p-16 text-center text-muted-foreground">City not found.</div>
  ),
  errorComponent: DirectoryRouteError,
  pendingComponent: DirectoryPending,
  component: ShopsCityPage,
});

function ShopsCityPage() {
  const { city } = Route.useParams() as { city: CitySlug };
  const { lang } = useT();
  const en = lang === "en";
  const c = CITIES[city];

  const { data: all } = useSuspenseQuery(activeShopsQuery);
  const shops = (all as Shop[]).filter((s) => shopMatchesCity(s, c));

  return (
    <main className="mx-auto max-w-6xl px-4 py-10">
      <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
        <ol className="flex flex-wrap items-center gap-1.5">
          <li>
            <Link to="/" className="hover:underline">
              {en ? "Home" : "হোম"}
            </Link>
          </li>
          <li aria-hidden>/</li>
          <li>
            <Link to="/shops" className="hover:underline">
              {en ? "Shops" : "দোকান"}
            </Link>
          </li>
          <li aria-hidden>/</li>
          <li className="text-foreground">{en ? c.name_en : c.name_bn}</li>
        </ol>
      </nav>

      <header className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">
          {en ? `Parts shops in ${c.name_en}` : `${c.name_bn}-এর পার্টস দোকান`}
        </h1>
        <p className="mt-2 text-muted-foreground">{c.eta}</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {en ? `${shops.length} verified shops` : `${shops.length}টি যাচাইকৃত দোকান`}
        </p>
      </header>

      {shops.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">
            {en
              ? `No verified sellers in ${c.name_en} yet — browse every shop or order with nationwide delivery.`
              : `${c.name_bn}-এ এখনও যাচাইকৃত বিক্রেতা নেই — সব দোকান দেখুন বা সারাদেশ ডেলিভারিতে অর্ডার করুন।`}
          </p>
          <Link
            to="/shops"
            className="mt-4 inline-flex h-10 items-center rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground"
          >
            {en ? "All shops" : "সব দোকান"}
          </Link>
        </div>
      ) : (
        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {shops.map((s) => (
            <li key={s.slug}>
              <Link
                to="/shops/$slug"
                params={{ slug: s.slug }}
                className="group flex h-full flex-col rounded-lg border border-border bg-card p-5 transition hover:border-primary hover:shadow-sm"
              >
                <div className="flex items-start gap-3">
                  {s.logo_url ? (
                    <img
                      {...shopLogoImgProps(s.logo_url, 48)}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      className="h-12 w-12 shrink-0 rounded-full border border-border object-cover"
                    />
                  ) : (
                    <div className="h-12 w-12 shrink-0 rounded-full bg-muted" aria-hidden />
                  )}
                  <div className="min-w-0 flex-1">
                    <h2 className="truncate text-base font-semibold group-hover:underline">
                      {pickLocalized({ name_en: s.name_en, name_bn: s.name_bn }, lang) || s.slug}
                    </h2>
                    {s.district ? (
                      <p className="mt-0.5 flex items-center gap-1 truncate text-sm text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5" aria-hidden />
                        {s.district}
                      </p>
                    ) : null}
                  </div>
                  {s.verified ? (
                    <ShieldCheck
                      className="h-5 w-5 shrink-0 text-primary"
                      aria-label="Verified seller"
                    />
                  ) : null}
                </div>
                {s.rating_count && s.rating != null ? (
                  <p className="mt-4 flex items-center gap-1.5 text-sm">
                    <Star className="h-4 w-4 fill-primary text-primary" aria-hidden />
                    <span className="font-medium">{Number(s.rating).toFixed(1)}</span>
                    <span className="text-muted-foreground">({s.rating_count})</span>
                  </p>
                ) : (
                  <p className="mt-4 text-sm text-muted-foreground">{en ? "New seller" : "নতুন"}</p>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}

      <section className="mt-14">
        <h2 className="text-lg font-semibold">{en ? "Other cities" : "অন্য শহর"}</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {CITY_SLUGS.filter((s) => s !== city).map((s) => (
            <Link
              key={s}
              to="/shops/city/$city"
              params={{ city: s }}
              className="rounded-full border border-border bg-card px-3 py-1.5 text-sm hover:border-primary"
            >
              {en ? CITIES[s].name_en : CITIES[s].name_bn}
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}
